import startCase from 'lodash/startCase';

import { DEFAULT_LABWARE_CONTENT } from '../constants';
import type {
  LabwareProperties,
  LabwarePropertiesForm,
  LabwareType,
} from '../types';
import { labwarePropertiesSchema } from '../zodSchemas/labwareProperties';

import { getClosureStateOptionsForLabwareType } from './';

export const getNickname = (labwareType: LabwareType): string => {
  if (labwareType === 'microplate') return 'Plate';
  return startCase(labwareType);
};

export function defaultLabwareProperties(args: {
  id: string;
  labwareType: LabwareType;
}): LabwareProperties {
  const { id, labwareType } = args;

  const closureStates = getClosureStateOptionsForLabwareType(labwareType);
  // TODO(IL, 2024-03-18): not sure how this data will be used, for now default
  // to the first closure state option for labware (or 'open' if there aren't any options, eg for tiprack)
  const closureState = closureStates[0] || 'open';

  return {
    id,
    labwareType,
    labwareDefinitionURI: null,
    nickname: getNickname(labwareType),
    content: DEFAULT_LABWARE_CONTENT,
    contentDescription: null,
    closureState,
    singleOrMultiUse: 'single',
    totalUnits: null,
    maxIdleTimeExists: false,
    maxIdleTime: null,
    startLocation: null,
    slot: '1',
  };
}

/** Deal with fields which conditionally change other fields in the labware properties form */
export function _processLabwareFieldChange(
  formUpdate: Partial<LabwareProperties>,
  prevProperties: LabwareProperties
): Partial<LabwareProperties> {
  let relatedChanges: Partial<LabwareProperties> = {};

  if (formUpdate.startLocation && prevProperties.slot !== '1') {
    relatedChanges.slot = '1';
  }

  if (
    formUpdate.singleOrMultiUse === 'single' &&
    prevProperties.singleOrMultiUse !== 'single'
  ) {
    relatedChanges.totalUnits = null;
  }

  return { ...formUpdate, ...relatedChanges };
}

/** Generate a patch to apply to LabwareProperties, given a change in one or more LabwarePropertiesForm fields. */
export function createLabwarePropertiesPatch(
  formUpdate: Partial<LabwarePropertiesForm>,
  prevProperties: LabwareProperties
): Partial<LabwareProperties> {
  const update = labwarePropertiesSchema.partial().safeParse(formUpdate);
  if (update.success) {
    return _processLabwareFieldChange(update.data, prevProperties);
  }
  return {};
}

/** Inverse of casting via the labwarePropertiesSchema, to populate the form fields from saved LabwareProperties */
export function labwarePropertiesToForm(
  labwareProperties: LabwareProperties
): LabwarePropertiesForm {
  return {
    labwareType: labwareProperties.labwareType,
    labwareDefinitionURI: labwareProperties.labwareDefinitionURI ?? '',
    nickname: labwareProperties.nickname,
    content: labwareProperties.content,
    contentDescription: labwareProperties.contentDescription ?? '',
    closureState: labwareProperties.closureState,
    singleOrMultiUse: labwareProperties.singleOrMultiUse,
    totalUnits: labwareProperties.totalUnits,
    maxIdleTimeExists: labwareProperties.maxIdleTimeExists,
    maxIdleTime: labwareProperties.maxIdleTime,
    startLocation: labwareProperties.startLocation ?? '',
    slot: labwareProperties.slot,
  };
}
